import { useEffect, useState } from "react";

// Supported view ids. Keep in sync with App.tsx's PAGES array; the
// pathSegment ↔ view mapping lives here because routing is a URL
// concern, not a nav-list concern.
export type View = "home" | "schedule" | "rpi" | "conferences" | "team";

export type StandingsMode = "overall" | "conference" | "non-conference";

const KNOWN_MODES = new Set<StandingsMode>([
  "overall",
  "conference",
  "non-conference",
]);
const DEFAULT_MODE: StandingsMode = "overall";

type URLState = {
  view: View;
  sport: string;
  division: string;
  // Standings-only filters. Carried in URL alongside sport/division
  // so a conference-scoped standings link stays shareable. Each
  // defaults to the "no filter" form; the Standings page is the only
  // reader, other views ignore these fields.
  mode: StandingsMode;
  conference: string;
  // Schedule-only date, YYYY-MM-DD. Empty means "today" and stays
  // off the URL so /schedule reads clean on the common case. The
  // ScoreboardPage is the only reader; other views ignore it.
  date: string;
  // Team-page identity. Only populated when view = "team"; empty for
  // every other route. Unlike the standings filters, this is PATH
  // state (not a query param) because a team is a resource identity,
  // not a filter — the URL reads /team/soccer-women/d1/unc.
  teamSlug: string;
};

// Path segments map 1:1 to a view, except the internal id "rpi"
// surfaces at /standings so shared links read naturally. The team
// view is dynamic (/team/<sport>/<division>/<slug>) and handled
// directly in buildURL, so it's omitted from this static map.
const PATH_FOR_VIEW: Record<Exclude<View, "team">, string> = {
  home: "/",
  schedule: "/schedule",
  rpi: "/standings",
  conferences: "/conferences",
};

// Legacy path offsets — the original three tabs (/yesterday, /today,
// /tomorrow) map to the unified Schedule tab with the corresponding
// calendar date pre-selected. Old shared links keep working; the next
// pushState canonicalises them to /schedule?date=YYYY-MM-DD.
const LEGACY_DAY_OFFSET: Record<string, number> = {
  "/yesterday": -1,
  "/today": 0,
  "/tomorrow": 1,
};

const VIEW_FOR_PATH: Record<string, View> = {
  "/": "home",
  "/home": "home",
  "/schedule": "schedule",
  "/yesterday": "schedule",
  "/today": "schedule",
  "/tomorrow": "schedule",
  "/standings": "rpi",
  "/conferences": "conferences",
};

const DEFAULT_SPORT = "soccer-women";
const DEFAULT_DIVISION = "d1";
// Every sport the ingest-worker produces data for. A URL carrying a
// sport slug not in this set (old links, hand-crafted garbage) falls
// back to DEFAULT_SPORT rather than rendering an empty page.
const KNOWN_SPORTS = new Set([
  "soccer-women",
  "soccer-men",
  "football",
  "basketball-women",
  "basketball-men",
]);
const KNOWN_DIVISIONS = new Set(["d1", "d2", "d3"]);

// defaultModeFor picks the implicit mode when the URL carries no
// explicit ?mode= param. A conference filter is a strong signal that
// the user is looking at conference-specific standings, so the
// Record column should default to the conference W-L-T rather than
// the overall record for teams in that conference. Explicit
// ?mode=overall or ?mode=non-conference still wins.
function defaultModeFor(conference: string): StandingsMode {
  return conference !== "" ? "conference" : DEFAULT_MODE;
}

// YMD regex gates the `?date=` param. Anything that's not a plausible
// YYYY-MM-DD plus a surviving Date roundtrip (catches Feb 30, month
// 13, etc.) falls back to "" which renders today.
const YMD_RE = /^\d{4}-\d{2}-\d{2}$/;

function isValidYMD(s: string): boolean {
  if (!YMD_RE.test(s)) return false;
  const [y, m, d] = s.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  return (
    dt.getUTCFullYear() === y &&
    dt.getUTCMonth() === m - 1 &&
    dt.getUTCDate() === d
  );
}

// todayYMD returns today's calendar date in the user's local
// timezone, YYYY-MM-DD. Local is deliberate: scoreboards are US sports
// and the user's "today" is their wall-clock day. Matches the
// ScoreboardPage date-math it eventually drives.
export function todayYMD(): string {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

// shiftYMD bumps a YYYY-MM-DD date by `days`, crossing month / year
// boundaries correctly. Done in UTC so DST transitions don't move the
// calendar date under us.
export function shiftYMD(ymd: string, days: number): string {
  const [y, m, d] = ymd.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + days);
  const yyyy = dt.getUTCFullYear();
  const mm = String(dt.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(dt.getUTCDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function parseLocation(loc: Location): URLState {
  const path = loc.pathname.replace(/\/+$/, "") || "/";
  const params = new URLSearchParams(loc.search);

  // Team path shape is /team/<sport>/<division>/<slug>. Sport and
  // division come from the path (resource identity), not query
  // params. If any segment is missing or invalid the route falls
  // back to the default view so a shared half-URL doesn't dead-end.
  if (path.startsWith("/team/")) {
    const parts = path.split("/").filter(Boolean); // ["team", sport, division, slug]
    if (parts.length === 4) {
      const [, sport, division, slug] = parts;
      if (KNOWN_SPORTS.has(sport) && KNOWN_DIVISIONS.has(division) && slug) {
        return {
          view: "team",
          sport,
          division,
          mode: DEFAULT_MODE,
          conference: "",
          date: "",
          teamSlug: slug,
        };
      }
    }
    // Malformed team path — fall through to default view.
  }

  const view = VIEW_FOR_PATH[path] ?? "home";
  const rawSport = params.get("sport") ?? "";
  const rawDivision = params.get("division") ?? "";
  const rawMode = params.get("mode") ?? "";
  const conference = (params.get("conference") ?? "").trim();
  // Date source: legacy path offset wins if present (so /yesterday
  // stays on yesterday even after canonicalisation), otherwise the
  // explicit ?date= param, otherwise "" (meaning today).
  let date = "";
  const legacyOffset = LEGACY_DAY_OFFSET[path];
  if (legacyOffset !== undefined) {
    date = shiftYMD(todayYMD(), legacyOffset);
  } else {
    const rawDate = (params.get("date") ?? "").trim();
    if (isValidYMD(rawDate)) date = rawDate;
  }
  return {
    view,
    sport: KNOWN_SPORTS.has(rawSport) ? rawSport : DEFAULT_SPORT,
    division: KNOWN_DIVISIONS.has(rawDivision) ? rawDivision : DEFAULT_DIVISION,
    mode: KNOWN_MODES.has(rawMode as StandingsMode)
      ? (rawMode as StandingsMode)
      : defaultModeFor(conference),
    conference,
    date,
    teamSlug: "",
  };
}

function buildURL(state: URLState): string {
  // Team view carries sport+division+slug in the path, not query —
  // keeps a shared team URL readable and lines up with how the
  // standings service's team-games endpoint accepts them.
  if (state.view === "team") {
    return `/team/${state.sport}/${state.division}/${state.teamSlug}`;
  }

  const path = PATH_FOR_VIEW[state.view];
  const params = new URLSearchParams();
  // Only emit params when they differ from defaults so clean deep-
  // links like /standings don't get polluted with the baseline
  // selection. Mode and conference are standings-only; they stay on
  // the URL even when the user flips to another tab so returning to
  // Standings restores their last filter.
  if (state.sport !== DEFAULT_SPORT) params.set("sport", state.sport);
  if (state.division !== DEFAULT_DIVISION) params.set("division", state.division);
  // Emit mode only when it differs from the implicit default for the
  // current conference state. Keeps canonical URLs clean while
  // preserving explicit overrides (e.g. ?conference=acc&mode=overall).
  if (state.mode !== defaultModeFor(state.conference)) {
    params.set("mode", state.mode);
  }
  if (state.conference !== "") params.set("conference", state.conference);
  // ?date= appears only on /schedule and only when the user navigated
  // away from today. Leaving it off for today keeps the canonical
  // schedule link short: /schedule.
  if (state.view === "schedule" && state.date !== "") {
    params.set("date", state.date);
  }
  const query = params.toString();
  return query ? `${path}?${query}` : path;
}

// conferenceStandingsHref returns the URL that opens Standings
// filtered to the given conference slug in the given sport+division.
// Exported so components elsewhere can emit <a href> values that
// behave correctly with middle-click / open-in-new-tab and are
// intercepted by navigateSPA for same-tab navigation.
export function conferenceStandingsHref(
  conference: string,
  sport: string,
  division: string,
): string {
  const params = new URLSearchParams();
  if (sport !== DEFAULT_SPORT) params.set("sport", sport);
  if (division !== DEFAULT_DIVISION) params.set("division", division);
  params.set("conference", conference);
  return `/standings?${params.toString()}`;
}

// teamDetailHref returns the URL for one team's detail page. Sport
// and division sit in the path (resource identity), not query, so
// shared links read as /team/soccer-women/d1/unc.
export function teamDetailHref(
  slug: string,
  sport: string,
  division: string,
): string {
  return `/team/${sport}/${division}/${slug}`;
}

// navigateSPA pushes `href` onto history and manually fires a
// popstate so the useURLState hook re-reads location. Use inside a
// click handler that calls preventDefault — the <a> element keeps a
// real href so middle-click and open-in-new-tab still work as
// expected; the SPA handles plain left-click without a full reload.
export function navigateSPA(href: string): void {
  window.history.pushState(null, "", href);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

// useURLState reads initial (view, sport, division) from the window
// URL, pushes updates to history on change, and listens for back /
// forward navigation. Three setters are returned instead of one
// combined setState so individual controls (nav button, sport
// select, division select) only re-render their own concern.
export function useURLState(): {
  view: View;
  setView: (v: View) => void;
  sport: string;
  setSport: (s: string) => void;
  division: string;
  setDivision: (d: string) => void;
  mode: StandingsMode;
  setMode: (m: StandingsMode) => void;
  conference: string;
  setConference: (c: string) => void;
  date: string;
  setDate: (d: string) => void;
  teamSlug: string;
} {
  const [state, setState] = useState<URLState>(() => parseLocation(window.location));

  // On popstate (back / forward / direct URL load with hash change)
  // re-read location so the UI matches the browser's URL.
  useEffect(() => {
    const onPop = () => setState(parseLocation(window.location));
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  // Push the current state into history whenever it changes AND the
  // new URL differs from what's already there. The equality check
  // prevents a popstate → setState → pushState loop and avoids
  // spurious history entries when state updates land in a batch.
  useEffect(() => {
    const target = buildURL(state);
    const current = window.location.pathname + window.location.search;
    if (target !== current) {
      window.history.pushState(null, "", target);
    }
  }, [state]);

  return {
    view: state.view,
    setView: (view: View) => setState((s) => ({ ...s, view })),
    sport: state.sport,
    setSport: (sport: string) => setState((s) => ({ ...s, sport })),
    division: state.division,
    setDivision: (division: string) => setState((s) => ({ ...s, division })),
    mode: state.mode,
    setMode: (mode: StandingsMode) => setState((s) => ({ ...s, mode })),
    conference: state.conference,
    setConference: (conference: string) =>
      setState((s) => ({ ...s, conference })),
    date: state.date,
    setDate: (date: string) => setState((s) => ({ ...s, date })),
    teamSlug: state.teamSlug,
  };
}
